import React, {useCallback, useMemo} from 'react';
import {useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import {promoCodeAPI, tariffAPI, IPromoCode, EditPromoCodeType, ITariff, getTariffOption} from 'entities/tariff';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import Select from 'shared/ui/Select/Select';
import InputName from 'shared/ui/Input/InputName';
import InputNumber from 'shared/ui/Input/InputNumber/InputNumber';
import DatePickerControlled from 'shared/ui/Date/DateRangePickerControlled/DatePickerControlled';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import {getFormattedDate} from 'shared/lib/datetime';

import styles from './PromoCodes.module.scss';

interface IPromoCodesFormProps {
    onSuccess: () => void;
    item?: IPromoCode;
    onClose: () => void;
}

interface IPromoCodesFormState {
    value: string;
    tariff: string;
    period: number;
    expireAt: Date;
    published: boolean;
}

const PromoCodesForm: React.FC<IPromoCodesFormProps> = ({item, onSuccess, onClose}) => {
    const defaultValues = useMemo(() => ({
        value: item?.value || '',
        tariff: item?.tariff,
        period: item?.period ?? 1,
        expireAt: item?.expire_at ? new Date(item.expire_at) : undefined,
        published: item ? item.published : true,
    }), [item]);

    const {register, control, handleSubmit, formState, setValue, watch} = useForm<IPromoCodesFormState>({
        defaultValues,
        mode: 'onChange',
    });

    const {isValid, isDirty, errors} = formState;
    const isNew = !item;
    const published = watch('published');

    const {data: tariffs} = tariffAPI.useGetTariffsQuery({field: 'price', direction: 'asc'});
    const [createPromoCode] = promoCodeAPI.useCreatePromoCodeMutation();
    const [updatePromoCode] = promoCodeAPI.useUpdatePromoCodeMutation();

    const getTariffOptionWithArchive = (tariff: ITariff) => {
        const option = getTariffOption(tariff);
        return {
            ...option,
            label: tariff.is_archive ? `${option.label} (арх.)` : option.label,
        };
    };

    const onSubmit = useCallback(async(formData: IPromoCodesFormState) => {
        try {
            const promoCodeData: EditPromoCodeType = {
                value: formData.value,
                tariff: formData.tariff,
                period: Number(formData.period),
                expire_at: getFormattedDate(formData.expireAt, {format: 'YYYY-MM-DD'}),
                published: formData.published,
            };

            if (isNew) {
                await createPromoCode(promoCodeData).unwrap();
                toast.info(t`Промокод успешно добавлен`);
            } else {
                await updatePromoCode({ id: item.id, ...promoCodeData }).unwrap();
                toast.info(t`Промокод успешно обновлен`);
            }

            onSuccess();
        } catch (error: any) {
            const errorMessage = error?.data?.message || t`Произошла ошибка при сохранении промокода`;
            toast.info(errorMessage);
        }
    }, [isNew, item?.id, createPromoCode, updatePromoCode, onSuccess]);

    const isSubmitDisabled = !isValid || (!isNew && !isDirty);

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <ModalBodyWrapper>
                <FormGroupWithError
                    className={styles.formBlock}
                    label="Значение"
                    error={errors.value?.message}
                >
                    <InputName
                        control={control}
                        name="value"
                        placeholder="Введите значение промокода"
                        register={register}
                        defaultValue={defaultValues.value}
                    />
                </FormGroupWithError>
                <FormGroupWithError
                    className={styles.formBlock}
                    label="Тариф"
                    error={errors.tariff?.message}
                >
                    {
                        tariffs
                        && <Select
                            rules={{required: 'Выберите тариф'}}
                            control={control}
                            name="tariff"
                            data={tariffs.map(getTariffOptionWithArchive)}
                            placeholder="Выберите тариф"
                            defaultValue={defaultValues.tariff}
                            searchable={false}
                        />
                    }
                </FormGroupWithError>
                <FormGroupWithError
                    className={styles.formBlock}
                    label="Период, дней"
                    error={errors.period?.message}
                >
                    <InputNumber
                        control={control}
                        name="period"
                        setValue={setValue}
                        min={1}
                        defaultValue={defaultValues.period}
                    />
                </FormGroupWithError>
                <FormGroupWithError
                    className={styles.formBlock}
                    label="Действует до"
                    error={errors.expireAt?.message}
                >
                    <DatePickerControlled
                        control={control}
                        name="expireAt"
                        rules={{required: 'Выберите дату окончания действия'}}
                        defaultValue={defaultValues.expireAt}
                        placeholder="Выберите дату"
                        oneTap
                    />
                </FormGroupWithError>
                <FormGroupWithError
                    className={styles.formBlock}
                    label="Статус"
                >
                    <StatusToggle
                        condition={published}
                        onClick={() => setValue('published', !published, {shouldDirty: true})}
                    />
                </FormGroupWithError>
            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={isNew ? t`Добавить` : t`Изменить`}
                primaryButtonType="submit"
                primaryButtonDisabled={isSubmitDisabled}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default React.memo(PromoCodesForm);
